import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash-es/debounce';
import { useApplicationContext } from 'contexts/ApplicationContext';
import i18nContext from 'components/i18n-context';
import { ACCOUNT_STATUS, ACCOUNT_TYPE, PAYMENT_PROVIDER } from 'components/constants';
import CustomSearchInput from 'components/CustomSearchInput';
import Loader from 'components/Loader';
import Button from 'uikit/Button/Button';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './AccountsFiltersBar.scss';

const AccountsFilterBar = ({
  isLoading,
  isInitialized,
  filters,
  totalElements,
  handleSetFilters,
  handleExportAccountList
}) => {
  const i18n = useContext(i18nContext);
  const [searchValue, setSearchValue] = useState(filters.searchText ? filters.searchText : '');
  const { constants } = useApplicationContext();

  const debouncedSearch = useRef(
    debounce((name, value) => {
      handleSetFilters(name, value);
    }, 400)
  ).current;

  const handleSearchTextInputChange = ({ target: { name, value } }) => {
    setSearchValue(value);

    debouncedSearch(name, value);
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const accountTypesOptions = Object.keys(ACCOUNT_TYPE).map((type) => {
    return {
      key: ACCOUNT_TYPE[type],
      value: i18n.getMessage('accountType.' + ACCOUNT_TYPE[type])
    };
  });

  const accountStatusOptions = Object.keys(ACCOUNT_STATUS).map((status) => {
    return {
      key: ACCOUNT_STATUS[status],
      value: i18n.getMessage('accountStatus.' + ACCOUNT_STATUS[status])
    };
  });

  const currencyOptions = constants?.currencies?.map((currency) => {
    return {
      key: currency,
      value: currency
    };
  });

  const transferProviderOptions = Object.keys(PAYMENT_PROVIDER).map((provider) => {
    return {
      key: PAYMENT_PROVIDER[provider].name,
      value: i18n.getMessage('paymentProvider.' + PAYMENT_PROVIDER[provider].name)
    };
  });

  return (
    <div className={'accounts-filters-wrapper'}>
      <div className={'accounts-actions-wrapper'}>
        <div className={'export-accounts-reports-actions-wrapper'}>
          <Button
            className={'accounts-button'}
            size={'medium'}
            type={'primary'}
            onClick={handleExportAccountList}
            isDisabled={!isInitialized || isLoading}
          >
            {isLoading ? <Loader /> : i18n.getMessage('accounts.button.accountsCsv')}
          </Button>
        </div>
      </div>

      <InputDropDown
        className={'accounts-filter-wrapper'}
        label={i18n.getMessage('accounts.filter.search.accountType.label')}
        name={'types'}
        value={filters.types}
        options={accountTypesOptions}
        onChange={handleSetFilters}
        isMulti
      />

      <InputDropDown
        className={'accounts-filter-wrapper'}
        label={i18n.getMessage('accounts.filter.search.accountStatus.label')}
        name={'statuses'}
        value={filters.statuses}
        options={accountStatusOptions}
        onChange={handleSetFilters}
        isMulti
      />

      <InputDropDownSearch
        className={'accounts-filter-wrapper-input-dropdown-search'}
        id={'currencies'}
        name={'currencies'}
        label={i18n.getMessage('accounts.filter.search.currency.label')}
        value={filters.currencies}
        options={currencyOptions}
        onChange={handleSetFilters}
        isDisabled={isLoading}
        isMulti
      />

      <InputDropDown
        className={'accounts-filter-wrapper'}
        name={'transferProviders'}
        label={i18n.getMessage('accounts.filter.search.transferProvider.label')}
        value={filters.transferProviders}
        options={transferProviderOptions}
        onChange={handleSetFilters}
        isMulti
      />

      <CustomSearchInput
        wrapperClassName={'search-wrapper'}
        inputClassName={'accounts-filters-wrapper'}
        label={i18n.getMessage('accounts.filter.search.label')}
        placeholder={i18n.getMessage('accounts.filter.search.placeholder')}
        name={'searchText'}
        value={searchValue}
        onChange={handleSearchTextInputChange}
        totalElements={totalElements}
      />
    </div>
  );
};

AccountsFilterBar.propTypes = {
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool,
  filters: PropTypes.object,
  totalElements: PropTypes.number,
  handleSetFilters: PropTypes.func,
  handleExportAccountList: PropTypes.func
};

export default AccountsFilterBar;
